import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * **Der Seitenverlauf** (CONTEXT.md, ADR 0034, #225): die Seiten, die man nacheinander angesehen
 * hat — über alle Lebensbereiche und Tabs hinweg, wie im Browser. Zurück/Vor springt darin und
 * nimmt Lebensbereich und Tab mit; das Springen selbst erledigt der Aufrufer (`show`).
 *
 * Eine Liste mit Zeiger: Ein neuer Besuch verwirft „Vor“, direkt aufeinanderfolgende Besuche
 * derselben Seite zählen einmal, vorn wird bei {@link NAVIGATION_HISTORY_CAP} gekappt. Auf dem Gerät
 * gemerkt (`localStorage`), über den Neustart hinaus; beim Abmelden gelöscht ({@link clearNavigationHistory}).
 */

/** Eine besuchte Seite, so gespeichert, wie sie in den `localStorage` passt (Icon als Name). */
export interface PageVisit {
  lifeSpace: string
  path: string
  title: string
  iconName?: string
}

export interface NavigationHistoryState {
  entries: PageVisit[]
  /** Wo man steht; −1 ohne Einträge. */
  index: number
}

export const NAVIGATION_HISTORY_KEY = 'navigationHistory'
export const NAVIGATION_HISTORY_CAP = 50
/** So viele Seiten zeigt die Liste je Richtung. */
const LIST_LENGTH = 10

const EMPTY: NavigationHistoryState = { entries: [], index: -1 }

/** Einen Besuch anhängen. Rein — ohne React, ohne Speicher. */
export function recordVisit(state: NavigationHistoryState, visit: PageVisit): NavigationHistoryState {
  if (state.entries[state.index]?.path === visit.path) return state
  const entries = [...state.entries.slice(0, state.index + 1), visit].slice(-NAVIGATION_HISTORY_CAP)
  return { entries, index: entries.length - 1 }
}

/** Die laufenden Seitenverläufe — `clearNavigationHistory` leert auch sie, nicht nur den Speicher. */
const clearListeners = new Set<() => void>()

/**
 * Beim Abmelden oder Kontowechsel (`wipeUserLocalState`): Speicher **und** den Verlauf der offenen
 * App leeren. Sonst schrieb der nächste Seitenwechsel die Seiten des vorigen Kontos zurück.
 */
export function clearNavigationHistory(): void {
  try { localStorage.removeItem(NAVIGATION_HISTORY_KEY) } catch { /* gesperrt */ }
  clearListeners.forEach(clear => clear())
}

function load(): NavigationHistoryState {
  try {
    const raw = localStorage.getItem(NAVIGATION_HISTORY_KEY)
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw) as NavigationHistoryState
    if (!Array.isArray(parsed.entries) || typeof parsed.index !== 'number') return EMPTY
    return { entries: parsed.entries, index: Math.min(parsed.index, parsed.entries.length - 1) }
  } catch {
    return EMPTY
  }
}

export interface ListedVisit {
  /** Stelle im Seitenverlauf — für {@link UseNavigationHistory.goTo}. */
  index: number
  visit: PageVisit
}

export interface UseNavigationHistoryParams {
  /** Die Seite, die gerade zu sehen ist (aktiver Tab). Ändert sie sich, ist das ein Besuch. */
  current: PageVisit | null
  /** Nur im Hauptfenster — ein abgerissenes Fenster hat keinen Seitenverlauf. */
  enabled: boolean
  /**
   * Schlägt eine Seite vor dem Sprung nach: `null`, wenn es sie nicht mehr gibt (gelöschtes
   * Projekt) — dann geht es zum nächsten Eintrag in dieselbe Richtung; sonst die Seite, wie sie
   * jetzt ist (ein umgezogenes Projekt mit neuem Lebensbereich und Pfad).
   */
  resolve: (visit: PageVisit) => Promise<PageVisit | null>
  /** Zeigt die Seite: Lebensbereich und Tab mitnehmen. */
  show: (visit: PageVisit) => void
}

export interface UseNavigationHistory {
  canGoBack: boolean
  canGoForward: boolean
  back: () => Promise<void>
  forward: () => Promise<void>
  /** Direkt zu einer Stelle (aus der Liste). */
  goTo: (index: number) => Promise<void>
  /** Die Seiten davor, nächste zuerst (höchstens zehn). */
  backList: ListedVisit[]
  /** Die Seiten danach, nächste zuerst (höchstens zehn). */
  forwardList: ListedVisit[]
}

export function useNavigationHistory({ current, enabled, resolve, show }: UseNavigationHistoryParams): UseNavigationHistory {
  const [state, setState] = useState<NavigationHistoryState>(() => (enabled ? load() : EMPTY))
  const stateRef = useRef(state)
  stateRef.current = state
  /** Der Pfad, zu dem gerade gesprungen wird — sein Eintreffen ist kein neuer Besuch. */
  const jumpingToRef = useRef<string | null>(null)

  const currentPath = current?.path ?? null
  const currentRef = useRef(current)
  currentRef.current = current
  useEffect(() => {
    const visit = currentRef.current
    if (!enabled || !visit) return
    if (jumpingToRef.current !== null) {
      const arrived = jumpingToRef.current === visit.path
      jumpingToRef.current = null
      if (arrived) return
    }
    setState(prev => recordVisit(prev, visit))
  }, [enabled, currentPath])

  useEffect(() => {
    if (!enabled) return
    try {
      if (state.entries.length === 0) localStorage.removeItem(NAVIGATION_HISTORY_KEY)
      else localStorage.setItem(NAVIGATION_HISTORY_KEY, JSON.stringify(state))
    } catch { /* voll oder gesperrt */ }
  }, [enabled, state])

  useEffect(() => {
    const clear = () => {
      jumpingToRef.current = null
      setState(EMPTY)
    }
    clearListeners.add(clear)
    return () => { clearListeners.delete(clear) }
  }, [])

  const resolveRef = useRef(resolve)
  resolveRef.current = resolve
  const showRef = useRef(show)
  showRef.current = show

  /** Ab `start` in Richtung `step` die erste Seite, die es noch gibt — und dorthin springen. */
  const jumpFrom = useCallback(async (start: number, step: 1 | -1) => {
    const { entries } = stateRef.current
    for (let i = start; i >= 0 && i < entries.length; i += step) {
      const resolved = await resolveRef.current(entries[i]!)
      if (!resolved) continue
      setState(prev => ({
        entries: prev.entries.map((e, j) => (j === i ? resolved : e)),
        index: i,
      }))
      if (resolved.path !== currentRef.current?.path) jumpingToRef.current = resolved.path
      showRef.current(resolved)
      return
    }
  }, [])

  const back = useCallback(() => jumpFrom(stateRef.current.index - 1, -1), [jumpFrom])
  const forward = useCallback(() => jumpFrom(stateRef.current.index + 1, 1), [jumpFrom])
  const goTo = useCallback(
    (index: number) => jumpFrom(index, index < stateRef.current.index ? -1 : 1),
    [jumpFrom],
  )

  const backList = useMemo(
    () => state.entries.slice(Math.max(0, state.index - LIST_LENGTH), Math.max(0, state.index))
      .map((visit, k) => ({ index: Math.max(0, state.index - LIST_LENGTH) + k, visit }))
      .reverse(),
    [state],
  )
  const forwardList = useMemo(
    () => state.entries.slice(state.index + 1, state.index + 1 + LIST_LENGTH)
      .map((visit, k) => ({ index: state.index + 1 + k, visit })),
    [state],
  )

  // Stabil, solange sich nichts ändert — `MainLayout` reicht ihn per Kontext an die Titlebar weiter.
  return useMemo(() => ({
    canGoBack: state.index > 0,
    canGoForward: state.index >= 0 && state.index < state.entries.length - 1,
    back,
    forward,
    goTo,
    backList,
    forwardList,
  }), [state, back, forward, goTo, backList, forwardList])
}
